import { get, startsWith } from 'lodash/fp';
import { getCurrentUser } from '../CurrentUser';

const GTAG = 'G-FT6P3MT182';
const SCRIPTID = 'ga-gtag';

export function isTrackingEnabled(): boolean {
    const currentUser = getCurrentUser();
    const isInProduction = startsWith('prod', get('company.server', currentUser));
    return (
        get('allowGoogleAnalytics', currentUser) &&
        isInProduction &&
        !currentUser.company.companySettings.optOutExternalServicesDataSync &&
        currentUser.company.server === 'prod-us'
    );
}

export async function getTracking(): Promise<Gtag.Gtag> {
    if (!isTrackingEnabled()) {
        throw new Error('Tracking is disabled');
    } else if (document.getElementById(SCRIPTID)) {
        return gtag;
    } else {
        install(GTAG);
        return gtag;
    }
}

// Following code based on this package: github.com/idmadj/ga-gtag/blob/master/src/index.js

const install = (trackingId: string) => {
    const { head } = document;
    const script = document.createElement('script');
    script.id = SCRIPTID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    head.insertBefore(script, head.firstChild);

    window.dataLayer = window.dataLayer || [];

    gtag('js', new Date());
    gtag('config', trackingId);

    const companyId = getCurrentUser().company.id;
    const userId = getCurrentUser().id;
    gtag('set', 'user_properties', {
        userId,
        companyId,
    });
};

const gtag: Gtag.Gtag = function () {
    // Can't use arrow func + destructuring as Google expects
    // arguments objects in dataLayer (not an array of arguments).
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
};

const CATEGORIES_NAMES = {
    REVIEWS_V2: 'Reviews v2',
    DASHBOARD: 'Dashboard',
    REQUEST_FEEDBACK: 'Request Feedback',
    USER_PROFILE: 'User Profile',
    USER_PROFILE_V2: 'User Profile v2',
    USER_PICKER: 'User Picker',
    USER_SELECTION: 'User Selection',
    COMPANY_DIRECTORY: 'Company Directory',
    SLACK_INTEGRATION: 'Slack Integration',
    SYSTEM: 'System',
    MEETINGS_CALENDAR_INTEGRATION: 'Meetings Calendar Integration',
    REVIEW_FOLLOW_UP_MEETING: 'Review Follow-Up Meeting',
    MEETINGS: 'Meetings',
    INVITE_USERS_MODAL: 'Invite Users Modal',
    INVITE_USERS_ONBOARDING_MODAL: 'Invite Users Onboarding Modal',
    SKIP_INVITE_USERS_ONBOARDING_MODAL: 'Skip Add Teammates Onboarding',
    HELPFUL_RESOURCES_ATTACHMENTS: 'Helpful Resources Attachments',
    '360_PROVIDE_SCREEN_V2': '360 writing experience v2',
    ENGAGEMENT_ADDON_TRIAL: 'Engagement Addon Trial Page',
    MESSAGE_STATISTICS: 'Message Statistics',
    AI_WRITING_ASSISTANT: 'AI Writing Assistant',
    AI_SUMMARY_ASSISTANT: 'AI Summary Assistant',
    AI_OBJECTIVE_ASSISTANT: 'AI Objective Assistant',
    AI_OBJECTIVE_COACH: 'AI Objectives Coach',
};

export const LABEL_PREFIXES = {
    '360_FEEDBACK_REQUEST_ID': '360 feedback requestId',
    '360_CYCLE_ID': '360 cycle id',
    USER_ID: 'user',
    FEEDBACK_REQUEST_ID: 'feedback request',
    FEEDBACK_PARTICIPANT_ID: 'participant id',
    REVIEW_CYCLE_ID: 'review cycle',
    REVIEW_ID: 'review',
    SURVEY_ID: 'survey',
    SURVEY_INSTANCE_ID: 'survey instance',
    ASSESSMENT_STATUS: 'reviewer assessment status',
};

type ReviewsCategoryNames =
    | 'review filters'
    | 'your reviews cycle card'
    | 'review action bar'
    | 'review title bar'
    | 'review view card'
    | 'review cycle overview card'
    | 'review cycle graph card'
    | 'review cycle create wizard'
    | 'review cycle overview tabs'
    | 'review cycle overview charts';

type NavigationCategoryNames =
    | 'navigation sidebar'
    | 'navigation footer'
    | 'navigation header'
    | 'navigation header search bar'
    | 'navigation header help'
    | 'navigation user menu'
    | 'notifications'
    | 'test drive mode';

type EmailCategoryNames = 'email customization card' | 'email customization modal' | 'email customization page';

type HelpfulResourcesCategoryNames =
    | 'helpful resources'
    | 'helpful resources resources'
    | 'helpful resources content'
    | 'helpful resources profile'
    | 'helpful resources guides and help'
    | 'helpful resources preview'
    | 'helpful resources attachments';

type FeedbackCategoryNames =
    | 'feedback card'
    | '360 todo item card'
    | 'feedback request wizard'
    | 'feedback give wizard'
    | '360 cycle wizard'
    | 'feedback filters'
    | '360 cycle overview'
    | '360 cycle overview status'
    | '360 cycle overview warnings'
    | '360 cycle overview table'
    | '360 cycle overview table row'
    | 'anytime feedback home widget';

type MeetingCategoryNames =
    | 'meeting card'
    | 'meeting space'
    | 'meeting space overview'
    | 'meeting insights'
    | 'meeting dedicated page'
    | 'shared meetings';

type ObjectiveCategoryNames =
    | 'objective card'
    | 'objective action bar'
    | 'objective cycle wizard'
    | 'Objective Key Results';

type HomeCategoryNames = 'todo item' | 'latest activity' | 'coming up section';

type PraiseCategoryNames = 'praise card' | 'praise card headline' | 'praise overview';

type SurveysCategoryNames =
    | 'your surveys page'
    | 'your surveys survey card'
    | 'survey answer page'
    | 'survey admin overview page'
    | 'survey admin overview card'
    | 'survey admin dashboard'
    | 'survey admin report tab'
    | 'survey admin comments tab'
    | 'survey create wizard';

type InsightsCategoryNames = 'retention page' | 'retention tenure' | 'retention turnover' | 'performance analytics';

export type CategoryNames =
    | NavigationCategoryNames
    | HelpfulResourcesCategoryNames
    | ReviewsCategoryNames
    | EmailCategoryNames
    | FeedbackCategoryNames
    | MeetingCategoryNames
    | ObjectiveCategoryNames
    | HomeCategoryNames
    | PraiseCategoryNames
    | SurveysCategoryNames
    | InsightsCategoryNames
    | 'todo items'
    | 'messages'
    | 'questionnaire'
    | 'automations overview card'
    | 'objective admin nav'
    | 'billing end subscription'
    | 'upselling'
    | 'reactions'
    | 'feedback quality survey'
    | 'activity stream'
    | 'team page'
    | 'company page'
    | 'feature disabled hint'
    | 'design refresh 2025'
    | 'Early Access Program'
    | 'Cycle Selection'
    | 'Box Chart'
    | 'Data Table'
    | 'Glossary Admin Page';

export type ActionName = string;

export type EventTrackingProps = {
    category: keyof typeof CATEGORIES_NAMES | CategoryNames;
    action?: ActionName;
    label?: string;
    value?: number;
    nonInteractive?: boolean;
};

/**
 * Track custom events via Google Analytics.
 * https://developers.google.com/analytics/devguides/collection/analyticsjs/events
 *
 * @param {Object} params - All strings lower case with spaces
 * @param {string} params.category - Category of the event (e.g. 'navigation sidebar'). Should include feature name.
 * @param {string} [params.action='click'] - A short description of the event (e.g. 'toggle', 'open', 'close', 'select', etc.)
 * @param {string} [params.label] - A label to provide additional information (e.g. name of the item that was clicked)
 * @param {number} [params.value] - A numeric value associated with the event (must be an integer)
 */
export async function trackEvent({ category, action = 'click', label, value }: EventTrackingProps) {
    let eventCategory = category;
    if (category in CATEGORIES_NAMES) {
        // @ts-ignore
        eventCategory = CATEGORIES_NAMES[category];
    }
    const roundedValue = value !== undefined ? Math.round(value) : value;

    if (__DEV__) {
        /* eslint-disable no-console */
        if (!Number.isInteger(value ?? 0)) {
            console.error(
                `[GA] value is ${value} but must be an integer! Non-integer values will be rounded to the nearest integer.`
            );
        }

        console.groupCollapsed(
            '[GA] track event:',
            [eventCategory, action, label].filter(v => v !== undefined).join(' > ')
        );
        console.table({
            category: eventCategory,
            action,
            label,
            value: roundedValue,
        });
        console.groupEnd();
        /* eslint-enable no-console */
    }

    if (!isTrackingEnabled()) {
        return;
    }

    try {
        const gTag = await getTracking();
        gTag('event', action, {
            event_category: eventCategory,
            event_action: action,
            event_label: label,
            event_value: roundedValue,
        });
    } catch {
        // don't handle this exception
    }
}
